/**
 * SEO utility — constants and helpers for unified meta tags.
 * Used by PageHead on: landing, explore, business profile.
 */

import { DEFAULT_LOCALE, SUPPORTED_LOCALES, prefixPathWithLocale } from "../i18n/localePaths";
import { SITE_URL, SUPPORT_EMAIL } from "./env";

export const SITE_NAME = "PromBiz";

/** Map UI locale (en, ru, he) → Open Graph locale tag (underscore form). */
const OG_LOCALE_BY_LANG = { en: "en_US", ru: "ru_RU", he: "he_IL" };

export function localeToOpenGraphLocale(lang) {
  if (!lang || typeof lang !== "string") return null;
  return OG_LOCALE_BY_LANG[lang.toLowerCase()] || null;
}

/** Base URL for canonical/og. Uses env in build, fallback to origin. */
export const getSiteUrl = () =>
  typeof window !== "undefined"
    ? window.location.origin
    : (SITE_URL || "https://prombiz.biz");

/** Build full page title: "Page Name | PromBiz" */
export const buildTitle = (pageTitle, suffix = SITE_NAME) =>
  pageTitle && pageTitle.includes(SITE_NAME)
    ? pageTitle
    : pageTitle
      ? `${pageTitle} | ${suffix}`
      : SITE_NAME;

/** Truncate description for meta (max ~160 chars) */
export const truncateDescription = (text, maxLen = 160) => {
  if (!text || typeof text !== "string") return "";
  return text.length <= maxLen ? text : `${text.slice(0, maxLen - 1)}…`;
};

/** Build canonical URL for path */
export const buildCanonical = (path) => {
  const base = getSiteUrl();
  const p = path?.startsWith("/") ? path : `/${path || ""}`;
  return `${base.replace(/\/$/, "")}${p}`;
};

/**
 * Build alternate hreflang paths (path only, no origin) for a canonical path **without** locale prefix,
 * e.g. basePath="/business/my-slug" → { en: "/en/business/my-slug", ... }.
 */
export function buildAlternateLocalePaths(basePathWithoutLocale) {
  const raw = basePathWithoutLocale?.startsWith("/")
    ? basePathWithoutLocale
    : `/${basePathWithoutLocale || ""}`;
  const out = {};
  for (const loc of SUPPORTED_LOCALES) {
    out[loc] = prefixPathWithLocale(raw, loc);
  }
  return out;
}

export function getDefaultLocaleForSeo() {
  return DEFAULT_LOCALE;
}

/**
 * Organization schema — identity of the company behind the site.
 * Attach to home/about/contact/landing pages.
 */
export function buildOrganizationSchema() {
  const base = getSiteUrl();
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: base,
    logo: `${base}/logo512.png`,
    sameAs: [
      "https://www.facebook.com/prombiz",
      "https://www.instagram.com/prombiz",
      "https://www.linkedin.com/company/prombiz",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: SUPPORT_EMAIL,
      availableLanguage: ["en", "ru", "he"],
    },
  };
}

/**
 * WebSite schema with SearchAction (Sitelinks Search Box eligibility).
 * Attach to home page only.
 */
export function buildWebsiteSchema() {
  const base = getSiteUrl();
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: base,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${base}/en/explore?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
    inLanguage: ["en", "ru", "he"],
  };
}

/**
 * BreadcrumbList schema — adds context for crawlers/search features.
 * @param {Array<{name, path}>} items — ordered crumbs; paths include locale.
 */
export function buildBreadcrumbSchema(items) {
  if (!Array.isArray(items) || !items.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: buildCanonical(it.path),
    })),
  };
}

/**
 * Build LocalBusiness JSON-LD for schema.org (local SEO).
 * @param {object} business — name, description, address, phone, email, etc.
 * @param {string} pageUrl — full canonical URL of the business page
 * @returns {object} JSON-LD script content
 */
export const buildLocalBusinessSchema = (business, pageUrl) => {
  if (!business?.name) return null;
  const base = typeof pageUrl === "string" && pageUrl.startsWith("http")
    ? new URL(pageUrl).origin
    : getSiteUrl();
  const url = typeof pageUrl === "string" && pageUrl.startsWith("http")
    ? pageUrl
    : buildCanonical(pageUrl || "/");

  const phone = business.phone || business.phoneNumber;
  const address = business.address || business.address_line1;
  const image = business.banner || business.img || business.coverImage;
  const imageUrl = image
    ? (image.startsWith("http") ? image : `${base.replace(/\/$/, "")}${image.startsWith("/") ? image : `/${image}`}`)
    : null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: business.name,
    url,
    ...(business.description || business.shortDescription
      ? { description: truncateDescription(business.description || business.shortDescription, 500) }
      : {}),
    ...(imageUrl ? { image: imageUrl } : {}),
    ...(phone ? { telephone: phone } : {}),
    ...(business.email ? { email: business.email } : {}),
    ...((address || business.city || business.location)
      ? {
          address: {
            "@type": "PostalAddress",
            ...(address ? { streetAddress: address } : {}),
            ...((business.city || business.location) ? { addressLocality: business.city || business.location } : {}),
            ...(business.country ? { addressCountry: business.country } : {}),
            ...(business.postalCode ? { postalCode: String(business.postalCode) } : {}),
          },
        }
      : {}),
    ...(business.latitude && business.longitude
      ? { geo: { "@type": "GeoCoordinates", latitude: business.latitude, longitude: business.longitude } }
      : {}),
  };

  const hours = business.openingHours || business.opening_hours || business.settings?.opening_hours;
  if (hours && Array.isArray(hours) && hours.length > 0) {
    const dayMap = { Monday: "Monday", Tuesday: "Tuesday", Wednesday: "Wednesday", Thursday: "Thursday", Friday: "Friday", Saturday: "Saturday", Sunday: "Sunday" };
    const to24 = (s) => {
      if (!s || typeof s !== "string") return null;
      const m = s.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i);
      if (!m) return null;
      let h = parseInt(m[1], 10);
      const min = m[2] ? parseInt(m[2], 10) : 0;
      if ((m[3] || "").toUpperCase() === "PM" && h < 12) h += 12;
      if ((m[3] || "").toUpperCase() === "AM" && h === 12) h = 0;
      return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
    };
    schema.openingHoursSpecification = hours
      .filter((h) => h?.day && h?.hours)
      .map((h) => {
        const parts = String(h.hours || "").split("-").map((x) => x.trim());
        return {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: dayMap[h.day] || h.day,
          opens: to24(parts[0]) || "09:00",
          closes: to24(parts[1]) || "18:00",
        };
      });
    if (schema.openingHoursSpecification.length === 0) delete schema.openingHoursSpecification;
  }

  const score = business.review?.score ?? business.aggregateRating?.rating;
  const count = business.review?.count ?? business.aggregateRating?.reviewCount;
  if (score != null && count != null) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(score),
      reviewCount: Number(count),
      bestRating: 5,
      worstRating: 1,
    };
  }

  if (Array.isArray(business.reviews) && business.reviews.length > 0) {
    const mapped = business.reviews
      .filter((r) => r && typeof r.text === "string" && r.text.trim())
      .slice(0, 8)
      .map((r) => {
        const item = {
          "@type": "Review",
          reviewRating: {
            "@type": "Rating",
            ratingValue: Math.min(5, Math.max(1, Number(r.rating) || 5)),
            bestRating: 5,
          },
          author: { "@type": "Person", name: (r.name && String(r.name).trim()) || "Customer" },
          reviewBody: truncateDescription(r.text.trim(), 500),
        };
        if (r.date && /^\d{4}-\d{2}-\d{2}/.test(String(r.date))) {
          item.datePublished = String(r.date).slice(0, 10);
        }
        return item;
      });
    if (mapped.length) schema.review = mapped;
  }

  const sameAsList = [];
  if (business.website) {
    sameAsList.push(business.website.startsWith("http") ? business.website : `https://${business.website}`);
  }
  if (business.social && typeof business.social === "object") {
    for (const v of Object.values(business.social)) {
      if (v && /^https?:\/\//i.test(String(v))) sameAsList.push(String(v));
    }
  }
  if (sameAsList.length) {
    schema.sameAs = [...new Set(sameAsList)];
  }

  if (Array.isArray(business.tags) && business.tags.length) {
    schema.knowsAbout = business.tags.slice(0, 12).map(String);
  }

  if (Array.isArray(business.serviceArea) && business.serviceArea.length) {
    schema.areaServed = business.serviceArea.slice(0, 10).map((name) => ({
      "@type": "AdministrativeArea",
      name: String(name),
    }));
  }

  if (Array.isArray(business.teams) && business.teams.length > 0) {
    schema.employee = business.teams
      .filter((t) => t && t.name)
      .slice(0, 12)
      .map((t) => {
        const p = {
          "@type": "Person",
          name: String(t.name).trim(),
          ...(t.position ? { jobTitle: String(t.position) } : {}),
        };
        if (t.avatar && /^https?:\/\//i.test(String(t.avatar))) {
          p.image = String(t.avatar);
        }
        return p;
      });
    if (schema.employee.length === 0) delete schema.employee;
  }

  return schema;
};
